/**
 * ReconcileView — BeyondCompare-style side-by-side comparison of time entries
 * across Webapp (Cosmos), AutoTask, and TimeTech systems.
 *
 * Directional filters let techs see what's missing from each system,
 * with push/import buttons to sync entries between them.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { CalendarTimeEntry, Resource } from '../../services/types';
import {
  CompanyOption,
  getReconcileData,
  createStandaloneTimeTechEntry,
  importTimeTechEntry,
  ReconcileData,
} from '../../services/api';
import { useToast } from '../../contexts/ToastProvider';
import { formatHours } from './calendarUtils';
import {
  reconcileEntries,
  groupByDate,
  calculateSummary,
  ReconciledEntry,
  ReconcileDayGroup,
  ReconcileSummary,
  timeTechHours,
} from './reconcileUtils';

interface ReconcileViewProps {
  currentDate: Date;
  selectedResourceId: number | null;
  resources: Resource[];
  companies: CompanyOption[];
  onAddEntry: (date: Date, company: CompanyOption | null, prefill?: { hoursWorked: number }) => void;
  onEditEntry: (entry: CalendarTimeEntry) => void;
}

type FilterMode = 'all' | 'mismatches' | 'missing_tt' | 'missing_webapp' | 'missing_at';

const shortDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function ReconcileView({
  currentDate,
  selectedResourceId,
  resources,
  onEditEntry,
}: ReconcileViewProps) {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rawData, setRawData] = useState<ReconcileData | null>(null);
  const [filterMode, setFilterMode] = useState<FilterMode>('mismatches');
  const [creatingTT, setCreatingTT] = useState<Set<string>>(new Set());
  const [importingTT, setImportingTT] = useState<Set<number>>(new Set());
  const [createResults, setCreateResults] = useState<Record<string, { success: boolean; message: string }>>({});
  const [importResults, setImportResults] = useState<Record<number, { success: boolean; message: string }>>({});
  const [bulkRunning, setBulkRunning] = useState(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  const resourceName = useMemo(() => {
    if (!selectedResourceId) return '';
    const r = resources.find(res => res.autoTaskId === selectedResourceId);
    return r ? `${r.firstName} ${r.lastName}` : '';
  }, [selectedResourceId, resources]);

  const loadData = useCallback(async () => {
    if (!selectedResourceId) return;
    setLoading(true);
    setError(null);
    setCreateResults({});
    setImportResults({});
    try {
      const data = await getReconcileData(year, month, selectedResourceId);
      setRawData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reconcile data');
    } finally {
      setLoading(false);
    }
  }, [year, month, selectedResourceId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Reconcile entries
  const reconciled = useMemo<ReconciledEntry[]>(() => {
    if (!rawData) return [];
    return reconcileEntries(rawData.webapp, rawData.autotask, rawData.timetech);
  }, [rawData]);

  const summary = useMemo<ReconcileSummary>(() => calculateSummary(reconciled), [reconciled]);

  // Filter counts
  const filterCounts = useMemo(() => ({
    all: reconciled.length,
    mismatches: reconciled.filter(e => e.matchStatus !== 'matched' || e.hoursMismatch).length,
    missing_tt: reconciled.filter(e => e.matchStatus === 'only_webapp' || (e.matchStatus === 'partial' && !e.timetech)).length,
    missing_webapp: reconciled.filter(e => e.matchStatus === 'only_timetech').length,
    missing_at: reconciled.filter(e => e.matchStatus === 'only_webapp' && !e.autotask).length,
  }), [reconciled]);

  // Apply filter
  const filtered = useMemo<ReconciledEntry[]>(() => {
    switch (filterMode) {
      case 'mismatches':
        return reconciled.filter(e => e.matchStatus !== 'matched' || e.hoursMismatch);
      case 'missing_tt':
        return reconciled.filter(e => e.matchStatus === 'only_webapp' || (e.matchStatus === 'partial' && !e.timetech));
      case 'missing_webapp':
        return reconciled.filter(e => e.matchStatus === 'only_timetech');
      case 'missing_at':
        return reconciled.filter(e => e.matchStatus === 'only_webapp' && !e.autotask);
      default:
        return reconciled;
    }
  }, [reconciled, filterMode]);

  const dayGroups = useMemo<ReconcileDayGroup[]>(() => groupByDate(filtered), [filtered]);

  const issueCount = summary.onlyWebapp + summary.onlyAutotask + summary.onlyTimetech + summary.hoursMismatches;

  // Create TimeTech entry for a webapp entry missing from TimeTech
  const handleCreateInTimeTech = async (entry: ReconciledEntry) => {
    if (!entry.webapp) return;
    const we = entry.webapp;
    const key = we.id;

    setCreatingTT(prev => new Set([...prev, key]));
    try {
      const numCompanyId = typeof we.companyId === 'string' ? parseInt(we.companyId, 10) : we.companyId;
      const isCustomOrTTSource = numCompanyId === 0 || we.source === 'timetech' || numCompanyId >= 1000000;

      await createStandaloneTimeTechEntry({
        resourceId: we.resourceId,
        companyId: numCompanyId,
        companyName: we.companyName,
        timetechClientId: isCustomOrTTSource && numCompanyId >= 1000000 ? numCompanyId : undefined,
        dateWorked: we.dateWorked,
        hoursWorked: we.hoursWorked,
        description: we.summaryNotes || '',
        ticketNumber: we.ticketNumber,
        cosmosEntryId: we.id,
      });
      setCreateResults(prev => ({ ...prev, [key]: { success: true, message: 'Created' } }));
    } catch (err) {
      setCreateResults(prev => ({
        ...prev,
        [key]: { success: false, message: err instanceof Error ? err.message : 'Failed' },
      }));
    } finally {
      setCreatingTT(prev => { const n = new Set(prev); n.delete(key); return n; });
    }
  };

  // Import a TimeTech entry into WebApp (Cosmos)
  const handleImportToWebapp = async (entry: ReconciledEntry) => {
    if (!entry.timetech) return;
    const tt = entry.timetech;
    const key = tt.TimeEntryId;

    setImportingTT(prev => new Set([...prev, key]));
    try {
      await importTimeTechEntry({
        timeEntryId: tt.TimeEntryId,
        userId: tt.UserId,
        clientId: tt.ClientId,
        dateEntered: tt.DateEntered,
        hours: tt.Hours,
        minutes: tt.Minutes,
        description: tt.Description,
        displayText: tt.DisplayText,
      });
      setImportResults(prev => ({ ...prev, [key]: { success: true, message: 'Imported' } }));
    } catch (err) {
      setImportResults(prev => ({
        ...prev,
        [key]: { success: false, message: err instanceof Error ? err.message : 'Failed' },
      }));
    } finally {
      setImportingTT(prev => { const n = new Set(prev); n.delete(key); return n; });
    }
  };

  // Bulk push all visible entries
  const handleBulkAction = async () => {
    setBulkRunning(true);
    let successCount = 0;
    let failCount = 0;

    if (filterMode === 'missing_tt') {
      // Push all webapp entries to TimeTech
      const toPush = filtered.filter(e => e.webapp && !createResults[e.webapp.id]?.success);
      for (const entry of toPush) {
        try {
          await handleCreateInTimeTech(entry);
          successCount++;
        } catch {
          failCount++;
        }
      }
      addToast(failCount === 0 ? 'success' : 'warning', `Pushed ${successCount} to TimeTech${failCount ? `, ${failCount} failed` : ''}`);
    } else if (filterMode === 'missing_webapp') {
      // Import all TimeTech entries to WebApp
      const toImport = filtered.filter(e => e.timetech && !importResults[e.timetech.TimeEntryId]?.success);
      for (const entry of toImport) {
        try {
          await handleImportToWebapp(entry);
          successCount++;
        } catch {
          failCount++;
        }
      }
      addToast(failCount === 0 ? 'success' : 'warning', `Imported ${successCount} to WebApp${failCount ? `, ${failCount} failed` : ''}`);
    }

    setBulkRunning(false);
    // Reload after bulk action
    if (successCount > 0) {
      await loadData();
    }
  };

  if (!selectedResourceId) {
    return (
      <div className="flex items-center justify-center h-full text-slate-500">
        <p>Select a technician to reconcile time entries</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-3" />
          <p className="text-sm text-slate-400">Loading from all systems...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-400 text-sm mb-2">{error}</p>
          <button onClick={loadData} className="text-xs text-blue-400 hover:text-blue-300">Retry</button>
        </div>
      </div>
    );
  }

  const timetechEnabled = rawData?.meta.timetechEnabled ?? false;
  const showBulkAction = (filterMode === 'missing_tt' || filterMode === 'missing_webapp') && filtered.length > 0;
  const bulkLabel = filterMode === 'missing_tt'
    ? `Push All to TimeTech (${filtered.filter(e => e.webapp && !createResults[e.webapp.id]?.success).length})`
    : `Import All to WebApp (${filtered.filter(e => e.timetech && !importResults[e.timetech.TimeEntryId]?.success).length})`;

  return (
    <div className="flex flex-col h-full">
      {/* Summary Bar */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-700 bg-slate-800/50 overflow-x-auto">
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700/50 rounded-lg min-w-0">
          <span className="text-xs text-slate-400">Webapp</span>
          <span className="text-sm font-medium text-slate-200">{formatHours(summary.totalHours.webapp)}</span>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700/50 rounded-lg min-w-0">
          <span className="text-xs text-slate-400">AutoTask</span>
          <span className="text-sm font-medium text-slate-200">{formatHours(summary.totalHours.autotask)}</span>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700/50 rounded-lg min-w-0">
          <span className="text-xs text-slate-400">TimeTech</span>
          <span className={`text-sm font-medium ${timetechEnabled && rawData?.meta.timetechUserMapped ? 'text-slate-200' : 'text-slate-500'}`}>
            {!timetechEnabled ? 'Disabled' : !rawData?.meta.timetechUserMapped ? 'No mapping' : formatHours(summary.totalHours.timetech)}
          </span>
        </div>
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg min-w-0 ${issueCount > 0 ? 'bg-amber-900/30' : 'bg-green-900/30'}`}>
          <span className="text-xs text-slate-400">Issues</span>
          <span className={`text-sm font-medium ${issueCount > 0 ? 'text-amber-400' : 'text-green-400'}`}>{issueCount}</span>
        </div>
        <div className="ml-auto text-xs text-slate-500">{resourceName}</div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-1 px-4 py-2 border-b border-slate-700/50 bg-slate-800/30 overflow-x-auto">
        {([
          { key: 'all' as FilterMode, label: 'All', count: filterCounts.all },
          { key: 'mismatches' as FilterMode, label: 'Mismatches', count: filterCounts.mismatches },
          { key: 'missing_tt' as FilterMode, label: 'Missing from TT', count: filterCounts.missing_tt, icon: '\u2192', color: 'text-purple-400' },
          { key: 'missing_webapp' as FilterMode, label: 'Missing from WebApp', count: filterCounts.missing_webapp, icon: '\u2190', color: 'text-blue-400' },
        ] as { key: FilterMode; label: string; count: number; icon?: string; color?: string }[]).map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilterMode(tab.key)}
            className={`px-3 py-1 text-xs rounded-md transition-colors whitespace-nowrap flex items-center gap-1 ${
              filterMode === tab.key
                ? 'bg-blue-600 text-white'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'
            }`}
          >
            {tab.icon && <span className={filterMode === tab.key ? 'text-white' : tab.color}>{tab.icon}</span>}
            {tab.label} ({tab.count})
          </button>
        ))}

        {/* Bulk Action Button */}
        {showBulkAction && (
          <button
            onClick={handleBulkAction}
            disabled={bulkRunning}
            className="ml-2 px-3 py-1 text-xs rounded-md bg-green-600/80 text-white hover:bg-green-500 disabled:opacity-50 transition-colors whitespace-nowrap"
          >
            {bulkRunning ? 'Working...' : bulkLabel}
          </button>
        )}

        <button
          onClick={loadData}
          className="ml-auto p-1 text-slate-500 hover:text-slate-300 transition-colors shrink-0"
          title="Refresh data"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Column Headers */}
      <div className="grid grid-cols-3 gap-px px-4 py-1.5 border-b border-slate-700/50 bg-slate-800/50 text-xs text-slate-500 font-medium">
        <div>Webapp (Cosmos)</div>
        <div>AutoTask</div>
        <div>TimeTech</div>
      </div>

      {/* Day-Grouped List */}
      <div className="flex-1 overflow-y-auto">
        {dayGroups.length === 0 ? (
          <div className="flex items-center justify-center h-full text-slate-500 text-sm">
            {filterMode === 'all' ? 'No time entries found for this month' :
             filterMode === 'mismatches' ? 'No issues found \u2014 all entries match!' :
             filterMode === 'missing_tt' ? 'No entries missing from TimeTech' :
             filterMode === 'missing_webapp' ? 'No entries missing from WebApp' :
             'No issues found'}
          </div>
        ) : (
          dayGroups.map(group => (
            <div key={group.dateStr} className="border-b border-slate-700/30">
              {/* Date Header */}
              <div className="sticky top-0 z-10 px-4 py-1.5 bg-slate-800 grid grid-cols-3 gap-px items-center">
                <div className="flex items-center justify-between pr-2">
                  <span className="text-sm text-slate-300">
                    {shortDays[group.date.getDay()]} {group.date.getDate()}
                  </span>
                  <span className="text-xs text-slate-500">{formatHours(group.totals.webapp)}</span>
                </div>
                <div className="flex justify-end pr-2">
                  <span className="text-xs text-slate-500">{formatHours(group.totals.autotask)}</span>
                </div>
                <div className="flex justify-end">
                  <span className="text-xs text-slate-500">{formatHours(group.totals.timetech)}</span>
                </div>
              </div>

              {/* Entries */}
              <div className="space-y-px">
                {group.entries.map((entry, idx) => (
                  <ReconcileRow
                    key={idx}
                    entry={entry}
                    timetechEnabled={timetechEnabled}
                    onEditEntry={onEditEntry}
                    onCreateInTimeTech={handleCreateInTimeTech}
                    onImportToWebapp={handleImportToWebapp}
                    isCreatingTT={entry.webapp ? creatingTT.has(entry.webapp.id) : false}
                    isImportingTT={entry.timetech ? importingTT.has(entry.timetech.TimeEntryId) : false}
                    createResult={entry.webapp ? createResults[entry.webapp.id] : undefined}
                    importResult={entry.timetech ? importResults[entry.timetech.TimeEntryId] : undefined}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Row Component
// ──────────────────────────────────────────────────────────────────

function ReconcileRow({
  entry,
  timetechEnabled,
  onEditEntry,
  onCreateInTimeTech,
  onImportToWebapp,
  isCreatingTT,
  isImportingTT,
  createResult,
  importResult,
}: {
  entry: ReconciledEntry;
  timetechEnabled: boolean;
  onEditEntry: (entry: CalendarTimeEntry) => void;
  onCreateInTimeTech: (entry: ReconciledEntry) => void;
  onImportToWebapp: (entry: ReconciledEntry) => void;
  isCreatingTT: boolean;
  isImportingTT: boolean;
  createResult?: { success: boolean; message: string };
  importResult?: { success: boolean; message: string };
}) {
  const borderColor = entry.matchStatus === 'matched' && !entry.hoursMismatch
    ? 'border-green-600/40'
    : entry.matchStatus === 'partial'
      ? 'border-amber-600/40'
      : entry.hoursMismatch
        ? 'border-red-600/40'
        : entry.matchStatus === 'only_timetech'
          ? 'border-purple-600/40'
          : 'border-slate-700/30';

  return (
    <div className={`grid grid-cols-3 gap-px px-4 py-1 border-l-2 ${borderColor}`}>
      {/* Webapp Column */}
      <div className="pr-2">
        {entry.webapp ? (
          <button
            onClick={() => onEditEntry(entry.webapp!)}
            className="w-full text-left px-2 py-1 rounded hover:bg-slate-700/50 transition-colors"
          >
            <div className="text-xs text-slate-300 truncate">{entry.webapp.companyName}</div>
            <div className="text-xs text-slate-500 truncate">{entry.webapp.summaryNotes}</div>
            <div className="text-xs font-medium text-slate-400">{formatHours(entry.webapp.hoursWorked)}</div>
          </button>
        ) : entry.timetech && !importResult ? (
          <div className="px-2 py-1 flex items-center gap-2">
            <span className="text-xs text-slate-600 italic">Missing</span>
            <button
              onClick={() => onImportToWebapp(entry)}
              disabled={isImportingTT}
              className="text-xs px-2 py-0.5 bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 rounded transition-colors disabled:opacity-50"
            >
              {isImportingTT ? '...' : 'Import'}
            </button>
          </div>
        ) : importResult ? (
          <div className="px-2 py-1">
            <span className={`text-xs ${importResult.success ? 'text-green-400' : 'text-red-400'}`}>
              {importResult.message}
            </span>
          </div>
        ) : (
          <div className="px-2 py-1">
            <span className="text-xs text-slate-600 italic">Missing</span>
          </div>
        )}
      </div>

      {/* AutoTask Column */}
      <div className="pr-2">
        {entry.autotask ? (
          <div className="px-2 py-1">
            <div className="text-xs text-slate-300 truncate">{entry.autotask.ticketNumber || `T${entry.autotask.ticketID}`}</div>
            <div className="text-xs text-slate-500 truncate">{entry.autotask.summaryNotes || entry.autotask.internalNotes || '(no notes)'}</div>
            <div className={`text-xs font-medium ${entry.hoursMismatch ? 'text-red-400' : 'text-slate-400'}`}>
              {formatHours(entry.autotask.hoursWorked)}
            </div>
          </div>
        ) : (
          <div className="px-2 py-1">
            <span className="text-xs text-slate-600 italic">Missing</span>
          </div>
        )}
      </div>

      {/* TimeTech Column */}
      <div>
        {entry.timetech ? (
          <div className="px-2 py-1">
            <div className="text-xs text-slate-300 truncate">{entry.timetech.DisplayText}</div>
            <div className="text-xs text-slate-500 truncate">{entry.timetech.Description}</div>
            <div className={`text-xs font-medium ${entry.hoursMismatch ? 'text-red-400' : 'text-slate-400'}`}>
              {formatHours(timeTechHours(entry.timetech))}
            </div>
          </div>
        ) : timetechEnabled ? (
          <div className="px-2 py-1 flex items-center gap-2">
            <span className="text-xs text-slate-600 italic">Missing</span>
            {entry.webapp && !createResult && (
              <button
                onClick={() => onCreateInTimeTech(entry)}
                disabled={isCreatingTT}
                className="text-xs px-2 py-0.5 bg-purple-600/20 text-purple-400 hover:bg-purple-600/30 rounded transition-colors disabled:opacity-50"
              >
                {isCreatingTT ? '...' : 'Push to TT'}
              </button>
            )}
            {createResult && (
              <span className={`text-xs ${createResult.success ? 'text-green-400' : 'text-red-400'}`}>
                {createResult.message}
              </span>
            )}
          </div>
        ) : (
          <div className="px-2 py-1">
            <span className="text-xs text-slate-700">N/A</span>
          </div>
        )}
      </div>
    </div>
  );
}
